class Breadcrumbs extends HTMLElement {
  static #LEFT = 'breadcrumbs--fade-left';
  static #RIGHT = 'breadcrumbs--fade-right';
  static #EPS = 1;

  #controller = new AbortController();
  #mounted = false;

  /** @type {HTMLElement|null} */
  #scrollWrap = null; // [data-bc-scroll]
  /** @type {HTMLElement|null} */
  #list = null; // [data-bc-list]
  /** @type {HTMLElement|null} */
  #startSentinel = null; // [data-bc-sentinel="start"]
  /** @type {HTMLElement|null} */
  #endSentinel = null; // [data-bc-sentinel="end"]

  /** @type {IntersectionObserver|null} */
  #io = null;
  /** @type {ResizeObserver|null} */
  #ro = null;

  #startInView = true;
  #endInView = true;

  #raf = 0;

  connectedCallback() {
    if (this.#mounted) return;
    this.#mounted = true;

    this.#scrollWrap = this.querySelector('[data-bc-scroll]');
    this.#list = this.querySelector('[data-bc-list]');
    this.#startSentinel = this.querySelector('[data-bc-sentinel="start"]');
    this.#endSentinel = this.querySelector('[data-bc-sentinel="end"]');

    if (
      !(this.#scrollWrap instanceof HTMLElement) ||
      !(this.#list instanceof HTMLElement) ||
      !(this.#startSentinel instanceof HTMLElement) ||
      !(this.#endSentinel instanceof HTMLElement)
    ) return;

    // Prime
    this.#seedFromScroll();
    this.#apply(true);

    if ('IntersectionObserver' in window) this.#setupIntersectionObserver();
    this.#setupScrollSafetyNet();
    this.#setupResizeObserver();

    // Layout settle passes (fixes first-paint weirdness)
    queueMicrotask(() => this.#refresh());
    requestAnimationFrame(() => this.#refresh());
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#mounted = false;

    this.#io?.disconnect();
    this.#io = null;

    this.#ro?.disconnect();
    this.#ro = null;

    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;

    this.#scrollWrap = null;
    this.#list = null;
    this.#startSentinel = null;
    this.#endSentinel = null;
  }

  #setupIntersectionObserver() {
    const root = this.#list;
    if (!root) return;

    this.#io = new IntersectionObserver(
      (entries) => {
        let changed = false;

        for (const entry of entries) {
          const which = entry.target.getAttribute('data-bc-sentinel');
          const inView = entry.isIntersecting;

          if (which === 'start' && inView !== this.#startInView) {
            this.#startInView = inView;
            changed = true;
          } else if (which === 'end' && inView !== this.#endInView) {
            this.#endInView = inView;
            changed = true;
          }
        }

        if (changed) this.#apply();
      },
      { root, threshold: 0, rootMargin: '0px' }
    );

    this.#io.observe(this.#startSentinel);
    this.#io.observe(this.#endSentinel);
  }

  #setupScrollSafetyNet() {
    const el = this.#list;
    if (!el) return;

    const { signal } = this.#controller;

    el.addEventListener(
      'scroll',
      () => {
        if (this.#raf) return;
        this.#raf = requestAnimationFrame(() => {
          this.#raf = 0;
          this.#seedFromScroll();
          this.#apply();

          // Safari/iOS tail settle
          setTimeout(() => {
            this.#seedFromScroll();
            this.#apply();
          }, 0);
        });
      },
      { passive: true, signal }
    );
  }

  #setupResizeObserver() {
    const el = this.#list;
    if (!el) return;

    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => this.#refresh());
      this.#ro.observe(el);
      return;
    }

    const { signal } = this.#controller;
    window.addEventListener('resize', this.#refresh, { signal });
  }

  #refresh = () => {
    if (!this.#list) return;
    this.#seedFromScroll();
    this.#apply(true);
  };

  #maxScroll() {
    const el = this.#list;
    if (!el) return 0;
    return Math.max(0, el.scrollWidth - el.clientWidth);
  }

  #seedFromScroll() {
    const el = this.#list;
    if (!el) return;

    const maxScroll = this.#maxScroll();
    const x = el.scrollLeft;

    if (maxScroll <= Breadcrumbs.#EPS) {
      this.#startInView = true;
      this.#endInView = true;
      return;
    }

    // Deterministic edge detection
    this.#startInView = x <= Breadcrumbs.#EPS;
    this.#endInView = x >= (maxScroll - Breadcrumbs.#EPS);
  }

  #apply(force = false) {
    const wrap = this.#scrollWrap;
    const el = this.#list;
    if (!wrap || !el) return;

    const maxScroll = this.#maxScroll();
    const hasOverflow = maxScroll > Breadcrumbs.#EPS;

    const wantLeft = hasOverflow && !this.#startInView;
    const wantRight = hasOverflow && !this.#endInView;

    if (!force) {
      const hasLeft = wrap.classList.contains(Breadcrumbs.#LEFT);
      const hasRight = wrap.classList.contains(Breadcrumbs.#RIGHT);
      if (wantLeft === hasLeft && wantRight === hasRight) return;
    }

    wrap.classList.toggle(Breadcrumbs.#LEFT, wantLeft);
    wrap.classList.toggle(Breadcrumbs.#RIGHT, wantRight);
  }
}

if (!customElements.get('breadcrumbs-nav')) {
  customElements.define('breadcrumbs-nav', Breadcrumbs);
}
